/**
 * A shared searchable/browsable picker that adds INTO a visible target
 * (#1466 comment 48362, recipe 2).
 *
 * ── THE BOUNDARY, which is the whole point of the recipe ────────────────────
 * The ruling ends with the clause that decides every type below: *"CPQ supplies
 * product/group/custom-line adapters; it does not make `catalog_product` a UI-
 * library concept."* So **no domain entity appears anywhere in this file**.
 * An item is an id, a label, optional secondary text and optional preview
 * content; the acts are whatever the host declared. There is no product, no
 * group, no line, no quote and no catalogue here — the ADAPTER knows those
 * words, and the day a second workbench adopts this it will bring different
 * ones. A UI library that has learned one solution's nouns has to be forked by
 * the second solution, which is how a shared recipe stops being shared.
 *
 * ── it is NOT `Lookup`, and the difference is not cosmetic ──────────────────
 * `controls/Lookup` is the transient overlay that RESOLVES A FIELD: it opens
 * anchored to the control that wants a value, you pick one, it closes. It owns
 * its own query, debounce and race-guard, and — measured on this branch, at 356
 * lines — the strings `refused`, `partial`, `loading` and `error` appear in it
 * exactly zero times. It has one answer for "what came back", which is right
 * for resolving a field and wrong for a governed catalogue.
 *
 * This is RESIDENT. It lives inside a workbench (typically as the body of a
 * `WorkbenchPanelRail` panel), stays open across many adds, and its search
 * survives the target changing underneath it. Three differences follow, and
 * each is tested:
 *
 *   1. **the TARGET is the reason this is not a combobox.** A combobox returns
 *      a value to whoever opened it. This one adds INTO something — "add to
 *      this group", "add to the root" — the target is on screen, and it is in
 *      the accessible NAME of the region and of every act button. Visible-only
 *      is not stated: two pickers open on one workbench (one on the root, one
 *      on a nested group) render two identical "Add" buttons, and a screen
 *      reader user given only "Add" cannot tell which one puts the item where.
 *   2. **changing the target must not lose the search.** The obvious
 *      implementation — `key={target.id}` on the field, or an effect that
 *      clears the query when the target moves — remounts the search and throws
 *      away both the typed text and the caret. The person then retypes the same
 *      search once per group they add to. This component therefore does
 *      NOTHING on a target change, and §3 is the guard that keeps it that way.
 *   3. **searching is the HOST's job.** The host may debounce, page, or make a
 *      governed server round trip that can itself be refused. So this component
 *      takes `results` and renders states; it never filters the array it was
 *      handed. Filtering locally would become a lie the moment the host paged:
 *      the person would be told "no results" for a row the server would have
 *      returned. §4 proves items that do not match the query still render.
 *
 * ── CONTROLLED, the way the sibling recipe is ───────────────────────────────
 * `query`, `target` and `previewId` are props with change callbacks, and that
 * is all the state there is. No route, no `localStorage`, no global context —
 * because a workbench can be mounted twice on one screen (a compare view), and
 * anything ambient would silently make those two instances one. §1 mounts two
 * and proves they are independent.
 *
 * ── the SIX finite states, and the two that get collapsed ───────────────────
 * `loading · ready · empty · partial · refused · error`, for the results and
 * again for the preview, because a preview is a governed read too.
 *
 *   · REFUSED IS NOT EMPTY. "You may not see this" and "there is nothing here"
 *     are different facts about the world, and a governed surface that renders
 *     both as a blank list tells a person their catalogue is missing when it is
 *     actually withheld. They then go and ask the wrong question of the wrong
 *     person.
 *   · PARTIAL IS REAL. A catalogue that answered for two of its three sources
 *     has not failed and has not succeeded. Showing the two it has WITH the
 *     sentence that says the third is missing beats both silently showing
 *     two-thirds and throwing away the two-thirds that arrived.
 *
 * There is deliberately no seventh `idle` state. This picker is BROWSABLE, so
 * the zero-query answer is a legitimate list; a host that has no browse list
 * says so with `empty` and its own sentence.
 *
 * ── capability: two facts, two presentations ────────────────────────────────
 * `permitted` is the caller's capability keys, or `null` meaning THIS HOST HAS
 * NO CAPABILITY MODEL — which is not the same as "nothing is permitted", and
 * collapsing them is how a surface comes to show everything or nothing by
 * accident.
 *
 *   · an ACT the caller does not hold at all is NOT RENDERED. They do not have
 *     the power; a row of controls that always explain themselves is noise.
 *   · an ITEM a held act may not be applied to KEEPS ITS CONTROL and carries
 *     the reason. Vanishing is the defect: the person sees a shorter list and
 *     concludes the item does not exist, when the truth is that it does and
 *     they may not add it HERE.
 *
 * The unavailable control uses `aria-disabled`, not `disabled`. `disabled`
 * removes it from the tab order, so the one person who most needs the
 * explanation — the keyboard user — is the one who could never reach it.
 *
 * ── Escape is NOT handled here, on purpose ──────────────────────────────────
 * The enclosing surface owns dismissal: `WorkbenchPanelRail`'s panel closes on
 * Escape and returns focus to the button that opened it. A picker that
 * swallowed Escape to clear its query would trap a person inside a panel whose
 * documented exit had stopped working.
 *
 * ── measurements ────────────────────────────────────────────────────────────
 * 44×44 target floor and 20–22px glyphs, #1465's ruled numbers, applied to
 * every row and every act control rather than to the ones that looked small.
 */
import {
  useCallback, useId, useMemo, useRef,
  type KeyboardEvent as ReactKeyboardEvent, type ReactNode,
} from 'react'
import { readableError } from '@solum/format'
import s from './GovernedPickerPanel.module.css'

/* What a refusal reads as when the host's own words were machine text (#3012). */
const REFUSED = 'You don’t have access to this.'

/**
 * One finite body state, over whatever a section's content happens to be.
 *
 * Generic because the results list and the preview answer the same six
 * questions about the same governed world, and giving them two hand-written
 * unions is how one of them quietly grows a seventh member nobody renders.
 */
export type PickerBodyState<T> =
  | { kind: 'loading' }
  | { kind: 'ready'; content: T }
  | { kind: 'empty'; message: string }
  /** Some of it arrived. What did is still worth showing. */
  | { kind: 'partial'; content: T; message: string }
  /** Authorisation said no. Say so, and say what it was. */
  | { kind: 'refused'; message: string }
  | { kind: 'error'; message: string }

export type PickerResults = PickerBodyState<readonly PickerItem[]>
export type PickerPreview = PickerBodyState<ReactNode>

/**
 * An item, opaque to this component.
 *
 * There is no `type`, no `entity` and no `kind` field, and that absence is the
 * ruling: the adapter that produced this row is the only thing that knows what
 * it is a row OF.
 */
export interface PickerItem {
  /** Stable within one result set. Also the preview and test identity. */
  id: string
  label: string
  /** A second line — a code, a path, a qualifier. The host decides. */
  secondary?: string
  /** Decoration. The label carries the meaning; this renders `aria-hidden`. */
  icon?: ReactNode
}

/** Where an act lands. Visible, and named in every act's accessible name. */
export interface PickerTarget {
  id: string
  label: string
}

/** Something the host can do to an item, with this target. */
export interface PickerAct {
  key: string
  /** The verb, e.g. "Add". Rendered, and the stem of the accessible name. */
  label: string
  /** Decoration, at the ruled 20–22px. */
  icon?: ReactNode
  /**
   * The capability required to hold this act AT ALL. Omitted = always held.
   * Not held → the act is not rendered for any item, because the person does
   * not have the power and does not need it explained on every row.
   */
  capability?: string
  /**
   * Why this act may not be applied to THIS item, or `null` when it may.
   * A reason returned here keeps the control and shows the sentence — an item
   * the caller may not add must not vanish into a shorter list.
   */
  unavailableFor?: (item: PickerItem) => string | null
  run: (item: PickerItem, target: PickerTarget) => void
}

/** Creating is an affordance the host declares, never an assumption. */
export interface PickerCreate {
  label: string
  capability?: string
  run: (query: string, target: PickerTarget) => void
}

export interface GovernedPickerPanelProps {
  /** What this picker is called; the stem of the region's accessible name. */
  title: string
  /** WHERE acts land. Named out loud, not only drawn. */
  target: PickerTarget
  /** Supply with `onTargetChange` (and 2+ entries) for an in-panel switcher. */
  targets?: readonly PickerTarget[]
  onTargetChange?: (target: PickerTarget) => void
  /** The switcher's label, e.g. "Add to". */
  targetLabel?: string

  /** Controlled. The HOST searches — debounce, paging and refusal are its job. */
  query: string
  onQueryChange: (query: string) => void
  searchPlaceholder?: string

  /** What came back. Rendered as-is: this component never filters it. */
  results: PickerResults

  /** In render order. The SOLUTION decides which acts exist. */
  acts: readonly PickerAct[]
  /** Capability keys the caller holds, or `null` for "no capability model". */
  permitted?: readonly string[] | null

  /** Controlled preview selection. `null` = nothing previewed. */
  previewId?: string | null
  onPreviewChange?: (id: string | null) => void
  /** Lazy: called only for the previewed item. Absent = no preview region. */
  renderPreview?: (item: PickerItem) => PickerPreview

  /** Absent = this host cannot create, and no create control is drawn. */
  create?: PickerCreate

  testId?: string
}

/**
 * Does this caller hold `capability`?
 *
 * `null` permitted is the load-bearing case: it means the host has no
 * capability model at all, so everything is held. Reading it as an empty set
 * would hide every governed act on every host that has not adopted one.
 */
export function holdsCapability(
  permitted: readonly string[] | null | undefined, capability: string | undefined,
): boolean {
  if (!capability) return true
  if (permitted == null) return true
  return permitted.includes(capability)
}

/** The acts this caller holds. Unheld acts are absent, not disabled. */
export function permittedActs(
  acts: readonly PickerAct[], permitted: readonly string[] | null | undefined,
): readonly PickerAct[] {
  return acts.filter((a) => holdsCapability(permitted, a.capability))
}

/** The items a state carries, if it carries any. `ready` and `partial` do. */
export function itemsOf(results: PickerResults): readonly PickerItem[] {
  return results.kind === 'ready' || results.kind === 'partial' ? results.content : []
}

export function GovernedPickerPanel({
  title, target, targets, onTargetChange, targetLabel = 'Add to',
  query, onQueryChange, searchPlaceholder = 'Search',
  results, acts, permitted = null,
  previewId = null, onPreviewChange, renderPreview,
  create, testId = 'picker',
}: GovernedPickerPanelProps) {
  const uid = useId()
  const listRef = useRef<HTMLUListElement>(null)

  const heldActs = useMemo(() => permittedActs(acts, permitted), [acts, permitted])
  const canCreate = create != null && holdsCapability(permitted, create.capability)

  /* The rendered rows are EXACTLY what the host returned. No `.filter(query)`
     lives in this component and none may be added: see the header, and §4. */
  const items = itemsOf(results)
  const previewed = previewId === null ? null : items.find((i) => i.id === previewId) ?? null

  const searchId = `${testId}-q-${uid}`
  const targetId = `${testId}-t-${uid}`

  /* Arrow keys rove the rows, which is what a list of candidates owes a
     keyboard: Tab reaches the list once, arrows choose inside it, and
     ArrowDown from the search field steps into the results rather than out of
     the panel. Only meaningful when the rows are focusable — they are buttons
     only when there is a preview for them to select, because a control that
     does nothing is a lie told in the tab order. */
  const rowsOf = () => [...(listRef.current?.querySelectorAll<HTMLButtonElement>('[data-row]') ?? [])]

  const rove = useCallback((event: ReactKeyboardEvent<HTMLElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
    const rows = rowsOf()
    if (rows.length === 0) return
    event.preventDefault()
    const at = rows.indexOf(document.activeElement as HTMLButtonElement)
    const next = event.key === 'Home' ? 0
      : event.key === 'End' ? rows.length - 1
        : event.key === 'ArrowDown' ? Math.min(rows.length - 1, at + 1)
          : Math.max(0, at - 1)
    rows[next]?.focus()
  }, [])

  const searchKeys = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'ArrowDown') return
    const first = rowsOf()[0]
    if (!first) return
    event.preventDefault()
    first.focus()
  }

  return (
    <section
      className={s.panel}
      data-testid={testId}
      /* The target is IN THE NAME, not only on the screen. Two of these open on
         one workbench are otherwise indistinguishable to anyone not looking. */
      aria-label={`${title}, adding to ${target.label}`}
    >
      <header className={s.head}>
        <h2 className={s.title}>{title}</h2>

        {targets && targets.length > 1 && onTargetChange ? (
          <div className={s.targetRow}>
            <label className={s.targetLabel} htmlFor={targetId}>{targetLabel}</label>
            <select
              id={targetId} className={s.targetSelect} value={target.id}
              data-testid={`${testId}-target`}
              onChange={(event) => {
                const next = targets.find((t) => t.id === event.target.value)
                /* Change the target and NOTHING ELSE. No query reset, no
                   remount: the search is the person's work, the target is
                   where it lands, and moving one must not destroy the other. */
                if (next) onTargetChange(next)
              }}
            >
              {targets.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
        ) : (
          <p className={s.targetRow} data-testid={`${testId}-target-static`}>
            <span className={s.targetLabel}>{targetLabel}</span>
            <strong className={s.targetName}>{target.label}</strong>
          </p>
        )}
      </header>

      <div className={s.search}>
        <label className={s.srOnly} htmlFor={searchId}>{`Search ${title}`}</label>
        <input
          id={searchId} className={s.searchInput} type="search" value={query}
          placeholder={searchPlaceholder}
          data-testid={`${testId}-search`}
          onKeyDown={searchKeys}
          /* Every keystroke, undebounced. The HOST decides whether that is a
             round trip, a debounce or a local index — this component cannot
             know which, and guessing is how a picker becomes unusable against a
             source it was never measured on. */
          onChange={(event) => onQueryChange(event.target.value)}
        />
      </div>

      <div className={s.results} data-state={results.kind} aria-busy={results.kind === 'loading'}>
        <StateNote state={results} testId={testId} />
        {items.length > 0 && (
          <ul className={s.list} ref={listRef} data-testid={`${testId}-list`}>
            {items.map((item) => (
              <li key={item.id} className={s.item} data-testid={`${testId}-item-${item.id}`}>
                {onPreviewChange && renderPreview ? (
                  <button
                    type="button" data-row className={s.row}
                    /* The roving handler lives on the ROW, not on the list:
                       the list is a `<ul>`, and hanging a key listener on a
                       non-interactive element both trips `jsx-a11y` and fires
                       for keys pressed anywhere inside it, including in a
                       control a future row might contain. */
                    onKeyDown={rove}
                    data-on={previewId === item.id || undefined}
                    aria-current={previewId === item.id || undefined}
                    onClick={() => onPreviewChange(previewId === item.id ? null : item.id)}
                  >
                    <RowText item={item} />
                  </button>
                ) : (
                  <div className={s.row} data-static><RowText item={item} /></div>
                )}

                <div className={s.acts}>
                  {heldActs.map((act) => {
                    const why = act.unavailableFor?.(item) ?? null
                    const whyId = `${testId}-why-${act.key}-${item.id}-${uid}`
                    return (
                      <span key={act.key} className={s.actCell}>
                        <button
                          type="button" className={s.act}
                          data-testid={`${testId}-act-${act.key}-${item.id}`}
                          /* `aria-disabled`, never `disabled`: the reason has to
                             stay reachable by the keyboard user it is for. */
                          aria-disabled={why !== null || undefined}
                          aria-describedby={why !== null ? whyId : undefined}
                          aria-label={`${act.label} ${item.label} to ${target.label}`}
                          onClick={() => { if (why === null) act.run(item, target) }}
                        >
                          {act.icon && <span className={s.icon} aria-hidden>{act.icon}</span>}
                          <span className={s.actLabel}>{act.label}</span>
                        </button>
                        {why !== null && (
                          <span className={s.reason} id={whyId}
                            data-testid={`${testId}-why-${act.key}-${item.id}`}>{why}</span>
                        )}
                      </span>
                    )
                  })}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {previewed && renderPreview && (
        <section className={s.preview} data-testid={`${testId}-preview`}
          aria-label={`Preview of ${previewed.label}`}>
          <PreviewBody state={renderPreview(previewed)} testId={testId} />
        </section>
      )}

      {canCreate && create && (
        <footer className={s.createRow}>
          <button type="button" className={s.create}
            data-testid={`${testId}-create`}
            aria-label={`${create.label} in ${target.label}`}
            onClick={() => create.run(query, target)}>{create.label}</button>
        </footer>
      )}
    </section>
  )
}

function RowText({ item }: { item: PickerItem }) {
  return (
    <>
      {item.icon && <span className={s.icon} aria-hidden>{item.icon}</span>}
      <span className={s.rowText}>
        <span className={s.rowLabel}>{item.label}</span>
        {item.secondary && <span className={s.rowSecondary}>{item.secondary}</span>}
      </span>
    </>
  )
}

/**
 * The sentence a state owes, for the states that have one.
 *
 * `ready` says nothing — the rows are the answer. Every other state does say
 * something, which is the property that stops any of the six rendering as a
 * blank box: `partial` prints its sentence ABOVE the rows it did get.
 */
function StateNote({ state, testId }: { state: PickerResults; testId: string }) {
  switch (state.kind) {
    case 'ready':
      return null
    case 'loading':
      return <p className={s.note} role="status" data-testid={`${testId}-note`}>Searching…</p>
    case 'empty':
      return <p className={s.note} data-kind="empty" role="status"
        data-testid={`${testId}-note`}>{state.message}</p>
    case 'partial':
      return <p className={s.note} data-kind="partial" role="status"
        data-testid={`${testId}-note`}>{state.message}</p>
    case 'refused':
      /* Not empty and not an error. Nothing is broken and nothing is missing:
         this person is not permitted to see it, and only saying so lets them
         ask the right question of the right person. */
      return <p className={s.note} data-kind="refused" role="status"
        data-testid={`${testId}-note`}>{state.message}</p>
    case 'error':
      return <p className={s.note} data-kind="error" role="alert"
        data-testid={`${testId}-note`}>{state.message}</p>
  }
}

/** The same six, for the preview — a preview is a governed read too. */
function PreviewBody({ state, testId }: { state: PickerPreview; testId: string }) {
  const note = (kind: string, message: string, alert = false) => (
    <p className={s.note} data-kind={kind} role={alert ? 'alert' : 'status'}
      data-testid={`${testId}-preview-note`}>{message}</p>
  )
  switch (state.kind) {
    case 'loading': return note('loading', 'Loading…')
    case 'ready': return <>{state.content}</>
    case 'empty': return note('empty', state.message)
    case 'partial': return <>{note('partial', state.message)}{state.content}</>
    case 'refused': return note('refused', readableError(state.message, REFUSED))
    case 'error': return note('error', readableError(state.message), true)
  }
}
